/**
 * DataTableTabs Component
 * 
 * Animated tabs with Framer Motion.
 * Supports keyboard navigation for accessibility.
 * Registers Cmd+1-N shortcuts when inside DataTableShortcuts.
 */

import { useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useDataTableShortcuts } from './DataTableShortcuts';
import { PendingIndicatorIcon } from '../icons';
import type { DataTableTab, DataTableTabsProps } from './types';

export type { DataTableTab, DataTableTabsProps };

export function DataTableTabs<T extends string = string>({
  tabs,
  value,
  onValueChange,
  disabled = false,
  className,
}: DataTableTabsProps<T>) {
  const shortcuts = useDataTableShortcuts();

  useEffect(() => {
    if (!shortcuts || disabled) return;
    
    const unregisterFns = tabs.map((tab, index) => {
      return shortcuts.register(index, () => !tab.disabled && onValueChange(tab.key), 'tab');
    });

    return () => unregisterFns.forEach(fn => fn());
  }, [shortcuts, tabs, onValueChange, disabled]);

  // Only handle Home/End for accessibility - arrow keys are reserved for table pagination
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, _currentIndex: number) => {
      const enabled = tabs.filter((tab) => !tab.disabled);
      let next: DataTableTab<T> | undefined;

      switch (event.key) {
        case 'Home':
          next = enabled[0];
          break;
        case 'End':
          next = enabled[enabled.length - 1];
          break;
      }

      if (next) {
        event.preventDefault();
        event.stopPropagation();
        onValueChange(next.key);
      }
    },
    [tabs, onValueChange]
  );

  return (
    <div
      role="tablist"
      aria-label="Status filter"
      className={cn('flex items-center', className)}
    >
      {tabs.map((tab, index) => {
        const isActive = value === tab.key;
        const isDisabled = disabled || tab.disabled;

        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.key}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onValueChange(tab.key)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            disabled={isDisabled}
            className={cn(
              'relative px-[18px] py-4 rounded-input font-inter text-sm leading-[1.5] antialiased transition-colors',
              'focus:outline-none',
              isActive
                ? 'bg-dinnerplate-white-light font-semibold text-off-black'
                : 'bg-transparent font-normal text-grey',
              isDisabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <span className="flex items-center justify-center gap-1.5">
              {tab.label}
              {tab.hasIndicator && <PendingIndicatorIcon className="flex-shrink-0" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
